import React, { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
}

/**
 * Rich Text Editor component using ReactQuill
 * Provides WYSIWYG editing for HTML content with formatting options
 * 
 * Note: ReactQuill uses findDOMNode internally, which causes a deprecation warning.
 * This is a known issue with the library and will be resolved when ReactQuill updates.
 * The warning does not affect functionality.
 */
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  readOnly = false,
  className = ''
}) => {
  // Ref for the editor container to help with DOM access
  const editorRef = useRef<HTMLDivElement>(null);

  // Configure toolbar options for email HTML editing
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link', 'image'
  ];

  return (
    <div ref={editorRef} className={`rich-text-editor ${className}`}>
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly || disabled}
        style={{
          backgroundColor: 'var(--color-surface-bg)',
          color: 'var(--color-surface-text)'
        }}
      />
      <style>{`
        .rich-text-editor .ql-container {
          font-family: Arial, sans-serif;
          font-size: 14px;
          min-height: 200px;
          background-color: var(--color-surface-bg);
          color: var(--color-surface-text);
        }
        .rich-text-editor .ql-editor {
          min-height: 200px;
        }
        .rich-text-editor .ql-toolbar {
          background-color: #f3f4f6;
          border: 1px solid var(--color-border);
          border-bottom: none;
        }
        .rich-text-editor .ql-container {
          border: 1px solid var(--color-border);
        }
        .rich-text-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
      `}</style>
    </div>
  );
};

